import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ChevronDown,
  FolderPlus,
  ImageIcon,
  LayoutGrid,
  List,
  Package,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { productsApi, categoriesApi } from '../services/api';
import ProductCategoryModal from '../components/ProductCategoryModal';
import ProductModal from '../components/ProductModal';
import './ProductsPage.css';

const PAGE_SIZE = 50;

const ProductsPage = () => {
  const { t } = useTranslation();

  // Data states
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null); // null = All
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);

  // View mode
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');

  // Mobile category dropdown
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);

  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesApi.list();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  /**
   * Loads one page of products. `append` keeps the already-loaded items so
   * infinite scroll accumulates instead of replacing the list.
   */
  const loadProducts = useCallback(async (pageNum, categoryId, append) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const response = await productsApi.list(pageNum, PAGE_SIZE, categoryId);
      if (!response.success) return;

      let data = response.data || [];
      let total = response.pagination?.total ?? data.length;
      let more = response.pagination?.has_more ?? false;
      let shouldAppend = append;

      // Older backends ignore the category filter. Detect that and fall back to
      // fetching everything once, then filtering locally.
      if (categoryId !== null && data.some((p) => Number(p.category_id) !== Number(categoryId))) {
        const allResp = await productsApi.list(1, 5000, null);
        const allData = allResp.success ? allResp.data || [] : [];
        data = allData.filter((p) => Number(p.category_id) === Number(categoryId));
        total = data.length;
        more = false;
        shouldAppend = false;
      }

      setProducts((prev) => (shouldAppend ? [...prev, ...data] : data));
      setTotalProducts(total);
      setHasMore(more);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const loadMoreProducts = useCallback(() => {
    if (loadingMore || loading || !hasMore) return;
    loadProducts(page + 1, selectedCategoryId, true);
  }, [loadingMore, loading, hasMore, page, selectedCategoryId, loadProducts]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Reload from page 1 whenever the active category changes
  useEffect(() => {
    loadProducts(1, selectedCategoryId, false);
  }, [selectedCategoryId, loadProducts]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const { scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight - scrollTop - clientHeight < 500) {
        loadMoreProducts();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreProducts]);

  // Close the mobile category menu on outside click / Escape
  useEffect(() => {
    if (!categoryDropdownOpen) return undefined;

    const onPointerDown = (e) => {
      if (!categoryDropdownRef.current?.contains(e.target)) setCategoryDropdownOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setCategoryDropdownOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [categoryDropdownOpen]);

  // Filter products by category and search
  const filteredProducts = products.filter((p) => {
    const matchesCategory =
      selectedCategoryId === null || Number(p.category_id) === Number(selectedCategoryId);
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      query === '' ||
      p.name?.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query);
    return matchesCategory && matchesSearch;
  });

  const getCategoryById = (id) => categories.find((c) => c.id === id);
  const activeCategory = getCategoryById(selectedCategoryId);

  const reloadCurrentView = () => {
    loadProducts(1, selectedCategoryId, false);
  };

  // Product actions
  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(t('products.confirmDelete', { name: product.name }))) return;

    try {
      const response = await productsApi.delete(product.id);
      if (response.success) reloadCurrentView();
    } catch (err) {
      alert(t('products.deleteError'));
    }
  };

  // Category actions
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(t('products.confirmDeleteCategory', { name: category.name }))) return;

    try {
      const response = await categoriesApi.delete(category.id);
      if (response.success) {
        fetchCategories();
        if (selectedCategoryId === category.id) setSelectedCategoryId(null);
        else reloadCurrentView();
      }
    } catch (err) {
      alert(t('products.deleteError'));
    }
  };

  return (
    <div className="products-page-modern">
      {/* Header */}
      <div className="products-header-modern">
        <div className="header-top">
          <div className="header-heading">
            <h1 className="page-title">{t('products.title')}</h1>
            <p className="page-subtitle">
              {totalProducts} {t('products.title')}
              {activeCategory ? ` · ${activeCategory.name}` : ''}
            </p>
          </div>

          <div className="header-actions-modern">
            <button
              type="button"
              className="btn-icon-modern"
              onClick={() => {
                setEditingCategory(null);
                setShowCategoryModal(true);
              }}
              title={t('products.categories')}
              aria-label={t('products.categories')}
            >
              <FolderPlus size={18} />
            </button>

            <div className="view-toggle-modern" role="group">
              <button
                type="button"
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                title={t('products.gridView')}
                aria-label={t('products.gridView')}
                aria-pressed={viewMode === 'grid'}
              >
                <LayoutGrid size={18} />
              </button>
              <button
                type="button"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                title={t('products.listView')}
                aria-label={t('products.listView')}
                aria-pressed={viewMode === 'list'}
              >
                <List size={18} />
              </button>
            </div>

            <button type="button" className="btn-add-modern" onClick={handleAddProduct}>
              <Plus size={18} />
              <span className="btn-text">{t('products.addProduct')}</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar-modern">
          <Search size={18} className="search-icon" aria-hidden="true" />
          <input
            type="search"
            placeholder={`${t('products.searchProducts')}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-modern"
            aria-label={t('products.searchProducts')}
          />
          {searchQuery && (
            <button
              type="button"
              className="clear-search"
              onClick={() => setSearchQuery('')}
              aria-label={t('common.clear', 'Clear')}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Mobile Category Dropdown */}
        <div className="category-dropdown-mobile" ref={categoryDropdownRef}>
          <button
            type="button"
            className="category-dropdown-trigger"
            onClick={() => setCategoryDropdownOpen((open) => !open)}
            aria-expanded={categoryDropdownOpen}
          >
            <span className="dropdown-icon">
              {selectedCategoryId === null ? '📦' : activeCategory?.icon || '📁'}
            </span>
            <span className="dropdown-text">
              {selectedCategoryId === null ? t('products.allProducts') : activeCategory?.name}
            </span>
            <span className="dropdown-count">{totalProducts}</span>
            <ChevronDown
              size={18}
              className={`dropdown-arrow ${categoryDropdownOpen ? 'open' : ''}`}
            />
          </button>

          {categoryDropdownOpen && (
            <div className="category-dropdown-menu">
              <button
                type="button"
                className={`dropdown-menu-item ${selectedCategoryId === null ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategoryId(null);
                  setCategoryDropdownOpen(false);
                }}
              >
                <span className="dropdown-item-icon">📦</span>
                <span className="dropdown-item-text">{t('products.allProducts')}</span>
              </button>

              {categories.map((category) => {
                const count =
                  category.product_count ??
                  products.filter((p) => p.category_id === category.id).length;
                return (
                  <button
                    key={category.id}
                    type="button"
                    className={`dropdown-menu-item ${
                      selectedCategoryId === category.id ? 'active' : ''
                    }`}
                    onClick={() => {
                      setSelectedCategoryId(Number(category.id));
                      setCategoryDropdownOpen(false);
                    }}
                  >
                    <span className="dropdown-item-icon">{category.icon || '📁'}</span>
                    <span className="dropdown-item-text">{category.name}</span>
                    <span className="dropdown-item-count">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Desktop Category Chips */}
        <div className="category-chips desktop-only">
          <button
            type="button"
            className={`category-chip ${selectedCategoryId === null ? 'active' : ''}`}
            onClick={() => setSelectedCategoryId(null)}
          >
            <span className="chip-icon">📦</span>
            <span className="chip-text">{t('products.allProducts')}</span>
            <span className="chip-count">{totalProducts}</span>
          </button>

          {categories.map((category) => {
            const count =
              category.product_count ??
              products.filter((p) => p.category_id === category.id).length;
            return (
              <div
                key={category.id}
                className={`category-chip ${selectedCategoryId === category.id ? 'active' : ''}`}
              >
                <button
                  type="button"
                  className="chip-main"
                  onClick={() => setSelectedCategoryId(Number(category.id))}
                >
                  <span className="chip-icon">{category.icon || '📁'}</span>
                  <span className="chip-text">{category.name}</span>
                  <span className="chip-count">{count}</span>
                </button>

                <span className="chip-actions">
                  <button
                    type="button"
                    onClick={() => handleEditCategory(category)}
                    className="chip-action-btn"
                    title={t('common.edit')}
                    aria-label={t('common.edit')}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteCategory(category)}
                    className="chip-action-btn danger"
                    title={t('common.delete')}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 size={13} />
                  </button>
                </span>
              </div>
            );
          })}

          <button
            type="button"
            className="category-chip add-category"
            onClick={() => {
              setEditingCategory(null);
              setShowCategoryModal(true);
            }}
          >
            <Plus size={15} />
            <span className="chip-text">{t('products.addCategory')}</span>
          </button>
        </div>
      </div>

      {/* Products Grid/List */}
      <div className="products-content-modern">
        {loading ? (
          <div className={`products-${viewMode}-modern`}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="product-card-modern is-skeleton">
                <div className="skeleton product-image-modern" />
                <div className="product-info-modern">
                  <div className="skeleton skeleton-line lg" />
                  <div className="skeleton skeleton-line" />
                  <div className="skeleton skeleton-line sm" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="empty-state">
            <span className="empty-state-icon error">
              <AlertCircle size={26} />
            </span>
            <h3>{t('common.error')}</h3>
            <p>{error}</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">
              <Package size={26} />
            </span>
            <h3>{t('products.noProducts')}</h3>
            <p>
              {searchQuery
                ? `${t('common.search')}: “${searchQuery}”`
                : selectedCategoryId
                ? t('products.noProducts')
                : t('products.addFirstProduct')}
            </p>
            {!searchQuery && !selectedCategoryId && (
              <button type="button" className="btn-add-modern" onClick={handleAddProduct}>
                <Plus size={18} />
                {t('products.addProduct')}
              </button>
            )}
          </div>
        ) : (
          <div className={`products-${viewMode}-modern`}>
            {filteredProducts.map((product) => {
              const category = getCategoryById(product.category_id);
              return (
                <article key={product.id} className="product-card-modern">
                  <div className="product-image-modern">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} loading="lazy" />
                    ) : (
                      <div className="product-placeholder-modern">
                        <ImageIcon size={26} />
                      </div>
                    )}

                    <div className="product-overlay">
                      <button
                        type="button"
                        className="overlay-btn"
                        onClick={() => handleEditProduct(product)}
                        title={t('common.edit')}
                        aria-label={t('common.edit')}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        className="overlay-btn danger"
                        onClick={() => handleDeleteProduct(product)}
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="product-badges">
                      {!product.in_stock && (
                        <span className="badge-modern out-of-stock">
                          {t('products.outOfStock')}
                        </span>
                      )}
                      {!product.is_active && (
                        <span className="badge-modern inactive">{t('products.inactive')}</span>
                      )}
                    </div>
                  </div>

                  <div className="product-info-modern">
                    <h3 className="product-name-modern">{product.name}</h3>

                    {product.description && (
                      <p className="product-description-modern">{product.description}</p>
                    )}

                    <div className="product-footer-modern">
                      {category && (
                        <span className="product-category-modern">
                          <span className="category-icon-small">{category.icon}</span>
                          <span className="category-name-small">{category.name}</span>
                        </span>
                      )}

                      {product.price && (
                        <span className="product-price-modern">
                          {product.price} {product.currency}
                          {product.has_custom_price && (
                            <span className="custom-badge" title={t('products.customPrice')}>
                              ★
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Load more */}
        {!loading && filteredProducts.length > 0 && (
          <div className="pagination-controls">
            {loadingMore && (
              <div className="loading-more">
                <div className="spinner-small"></div>
                <span>{t('common.loading')}</span>
              </div>
            )}

            {!loadingMore && hasMore && (
              <button type="button" className="btn-load-more" onClick={loadMoreProducts}>
                <ChevronDown size={18} />
                <span>{t('products.loadMore')}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCategoryModal && (
        <ProductCategoryModal
          category={editingCategory}
          onClose={() => {
            setShowCategoryModal(false);
            setEditingCategory(null);
          }}
          onSave={() => {
            fetchCategories();
            reloadCurrentView();
            setShowCategoryModal(false);
            setEditingCategory(null);
          }}
        />
      )}

      {showProductModal && (
        <ProductModal
          product={editingProduct}
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          onClose={() => {
            setShowProductModal(false);
            setEditingProduct(null);
          }}
          onSave={() => {
            reloadCurrentView();
            fetchCategories();
            setShowProductModal(false);
            setEditingProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default ProductsPage;
